import AssignmentMapController from "./delivery_assignment_map_controller"
import Sortable from "sortablejs"
import { depotMapMarker, numberedMapMarker } from "../utils/numbered_map_marker"
import { optimizeShipmentRun } from "../utils/optimize_shipment_run"
import { clockMinutes, formatRunTime, receivingHours, scheduleContext, scheduleRun } from "../utils/shipment_run_schedule"
import { ShipmentRunRoutes, routePoint, routeErrorMessage, routeSetupUrl } from "../utils/shipment_run_routes"

export default class extends AssignmentMapController {
  static targets = [...AssignmentMapController.targets, "travelDialog", "travelBody", "assignmentsField", "stopOrdersField", "list", "counts", "search", "bulkRun", "dirty", "pageLabel", "generationSummary", "generateButton", "reviewDialog", "reviewBody", "bulkPanel", "previous", "next", "issues", "layout", "sortStatus", "resetOrder", "autoSort", "undoSort", "sortResult", "depotDialog", "depotAddress", "depotLatitude", "depotLongitude", "depotError", "saveDepot", "depotLabel", "routesButton", "routesPanel", "routesSummary", "retryRoutes", "runSettingsField", "timingControls", "planOptions", "departure", "departureLabel", "sortMethod", "applyTimeAll", "timingSummary", "retryEstimates", "cancelSort", "sortDialog", "sortBody"]
  static values = { ...AssignmentMapController.values, rows: Array, runs: Array, assignments: Object, stopOrders: Object, depot: Object, depotUrl: String, readonly: Boolean, runSettings: Object, date: String, timeZone: String, partial: Boolean }

  connect() {
    this.rows = this.rowsValue
    this.runs = this.runsValue
    this.runLookup = new Map(this.runs.map(run => [run.id, run]))
    this.proposals = { ...this.assignmentsValue }
    this.stopOrders = { ...this.stopOrdersValue }
    this.runSettings = structuredClone(this.runSettingsValue)
    this.estimates = new Map()
    this.roadRoutes = new ShipmentRunRoutes()
    this.routeLines = []
    this.routePaths = []
    this.routesShown = false
    this.selected = new Set()
    this.focusedShipmentIds = new Set()
    this.expandedShipmentIds = new Set()
    this.runFilter = "all"
    this.page = 0
    this.beforeUnload = event => { if (this.dirty) { event.preventDefault(); event.returnValue = "" } }
    window.addEventListener("beforeunload", this.beforeUnload)
    if (this.hasBulkRunTarget) this.populateRuns(this.bulkRunTarget)
    this.refreshWorkspace()
    super.connect()
  }

  disconnect() {
    cancelAnimationFrame(this.focusScrollFrame)
    this.mapClickListener?.remove()
    this.mapClickListener = null
    cancelAnimationFrame(this.cancelDragFrame)
    this.clearPageDrag()
    this.clearRoadRoutes()
    this.sortAbort?.abort()
    this.depotAbort?.abort()
    this.estimates.forEach(state => { clearTimeout(state.timer); state.abort?.abort() })
    this.sortable?.destroy()
    window.removeEventListener("beforeunload", this.beforeUnload)
    if (this.hasReviewDialogTarget) window.bootstrap?.Modal.getInstance(this.reviewDialogTarget)?.dispose()
    if (this.hasDepotDialogTarget) window.bootstrap?.Modal.getInstance(this.depotDialogTarget)?.dispose()
    if (this.hasTravelDialogTarget) window.bootstrap?.Dialog.getInstance(this.travelDialogTarget)?.dispose()
    super.disconnect()
  }

  initializeMap() {
    super.initializeMap()
    if (this.mapInitialized && !this.mapTarget.hidden) {
      this.mapFailed = false
      this.map().setOptions({ mapTypeControl: false, streetViewControl: false })
      this.mapClickListener ||= this.map().addListener("click", () => this.clearMarkerFocus())
      this.mapStatus()
      this.refreshRoadRoutes()
      this.refreshEstimates()
    }
  }

  mapStatus() {
    const count = this.filteredRows().filter(row => row.position).length
    this.statusTarget.textContent = `${count} mapped${count < this.filteredRows().length ? ` · ${this.filteredRows().length - count} without pins` : ""}`
  }

  mapUnavailable() {
    this.mapFailed = true
    this.mapTarget.hidden = true
    this.statusTarget.textContent = "Map unavailable. Use the shipment list."
    this.refreshRoadRoutes()
  }

  filteredRows() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase()
    return this.rows.filter(row => {
      const run = this.proposals[String(row.shipment_id)] || "unassigned"
      return (this.runFilter === "all" || run === this.runFilter) &&
        (!query || [row.number, row.client, row.location, row.address, row.order_number].join(" ").toLocaleLowerCase().includes(query))
    }).sort((a, b) => this.compareRows(a, b))
  }

  compareDefaults(a, b) {
    const priority = row => String(row.default_run_id) === this.proposals[String(row.shipment_id)] ? row.default_priority ?? Infinity : Infinity
    return (priority(a) - priority(b)) || ((a.client_sort_order ?? Infinity) - (b.client_sort_order ?? Infinity)) || a.client.localeCompare(b.client) || String(a.stop_key).localeCompare(String(b.stop_key))
  }

  compareRows(a, b) {
    const byNumber = () => a.number.localeCompare(b.number, undefined, { numeric: true })
    const position = row => this.stopPositions.get(this.proposals[String(row.shipment_id)])?.get(row.stop_key) ?? Infinity
    return (position(a) - position(b)) || this.compareDefaults(a, b) || byNumber()
  }

  defaultStopKeys(runId) {
    return [...new Set(this.rows.filter(row => this.proposals[String(row.shipment_id)] === runId).sort((a, b) => this.compareDefaults(a, b)).map(row => row.stop_key))]
  }

  prepareStopOrders() {
    this.stopPositions = new Map()
    this.runs.forEach(run => {
      const available = this.defaultStopKeys(run.id)
      const saved = this.stopOrders[run.id]
      if (saved) {
        if (available.length) this.stopOrders[run.id] = [...saved.filter(key => available.includes(key)), ...available.filter(key => !saved.includes(key))]
        else delete this.stopOrders[run.id]
      }
      this.stopPositions.set(run.id, new Map((this.stopOrders[run.id] || available).map((key, index) => [key, index])))
    })
    this.stopOrdersFieldTarget.value = JSON.stringify(this.stopOrders)
  }

  canReorder() {
    return !this.readonlyValue && !this.sorting && this.runLookup.has(this.runFilter) && !this.searchTarget.value.trim()
  }

  refreshWorkspace() {
    this.prepareStopOrders()
    this.refreshEstimates()
    this.renderTimingControls()
    this.assignmentsFieldTarget.value = JSON.stringify(this.proposals)
    this.renderCounts()
    const visibleIds = new Set(this.filteredRows().map(row => String(row.shipment_id)))
    this.focusedShipmentIds = new Set([...this.focusedShipmentIds].filter(id => visibleIds.has(id)))
    this.renderList()
    const groups = new Map()
    this.filteredRows().filter(row => row.position).forEach(row => {
      const key = `${row.position.latitude},${row.position.longitude}`
      if (!groups.has(key)) groups.set(key, { latitude: row.position.latitude, longitude: row.position.longitude, shipments: [] })
      groups.get(key).shipments.push(row)
    })
    this.markersValue = [...groups.values()].map(marker => {
      const colors = [...new Set(marker.shipments.map(row => this.runLookup.get(this.proposals[String(row.shipment_id)])?.color || "#6c757d"))]
      const numbers = this.runLookup.has(this.runFilter) ? [...new Set(marker.shipments.map(row => this.stopPositions.get(this.runFilter).get(row.stop_key) + 1))].sort((a, b) => a - b) : []
      return { ...marker, stop_numbers: numbers, color: colors.length === 1 ? colors[0] : "#6c757d", title: `${marker.shipments.length} ${marker.shipments.length === 1 ? "shipment" : "shipments"}` }
    })
    if (this.validDepot()) this.markersValue = [...this.markersValue, { ...this.depotValue, depot: true, shipments: [], title: `Depot: ${this.depotValue.address}` }]
    this.centerValue = this.markersValue[0] || { latitude: 0, longitude: 0 }
    if (this.mapInitialized) {
      this.renderMarkers()
      if (!this.mapFailed) this.mapStatus()
    } else if (window.google?.maps?.Map) this.initializeMap()
    if (!this.markersValue.length) {
      this.mapTarget.hidden = true
      this.statusTarget.textContent = "No map pins. Use the shipment list."
    } else if (this.mapInitialized && !window.googleMapsAuthFailed && !this.mapFailed) this.mapTarget.hidden = false
    this.renderGenerationSummary()
    this.selectionChanged()
    this.refreshRoadRoutes()
  }

  renderCounts() {
    this.countsTarget.replaceChildren()
    const counts = this.rows.reduce((result, row) => {
      const key = this.proposals[String(row.shipment_id)] || "unassigned"
      result[key] = (result[key] || 0) + 1
      return result
    }, {})
    const visibleRuns = this.runs.filter(run => counts[run.id] || run.id === this.runFilter)
    const options = [{ id: "all", name: "All", count: this.rows.length }, { id: "unassigned", name: "Unassigned", count: counts.unassigned || 0 }, ...visibleRuns.map(run => ({ ...run, count: counts[run.id] || 0 }))]
    options.forEach(run => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = `btn btn-sm ${this.runFilter === run.id ? "btn-solid theme-primary" : "btn-outline theme-secondary"}`
      button.setAttribute("aria-pressed", String(this.runFilter === run.id))
      button.dataset.runFilter = run.id
      if (run.color) {
        const dot = document.createElement("span")
        dot.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:6px;background-color:${run.color}`
        button.append(dot)
      }
      button.append(document.createTextNode(`${run.name} · ${run.count}`))
      button.addEventListener("click", () => { if (this.sorting) return; this.runFilter = run.id; this.page = 0; this.selected.clear(); this.focusedShipmentIds.clear(); this.refreshWorkspace(); this.fitLocations() })
      this.countsTarget.append(button)
    })
  }

  renderList() {
    cancelAnimationFrame(this.cancelDragFrame)
    this.clearPageDrag()
    this.sortable?.destroy()
    this.sortable = null
    const rows = this.filteredRows()
    this.page = Math.min(this.page, Math.max(0, Math.ceil(rows.length / 100) - 1))
    const visible = rows.slice(this.page * 100, (this.page + 1) * 100)
    const reorder = this.canReorder()
    this.listTarget.replaceChildren()
    visible.forEach(row => this.listTarget.append(this.buildShipmentRow(row, reorder)))
    if (!visible.length) this.addText(this.listTarget, "No shipments match this filter.", "fg-2 p-4")
    this.pageLabelTarget.textContent = rows.length ? `${this.page * 100 + 1}–${this.page * 100 + visible.length} of ${rows.length}` : "0 shipments"
    this.previousTarget.hidden = rows.length <= 100
    this.previousTarget.disabled = this.page === 0
    this.nextTarget.hidden = rows.length <= 100
    this.nextTarget.disabled = (this.page + 1) * 100 >= rows.length
    if (this.hasResetOrderTarget) this.resetOrderTarget.disabled = !this.runLookup.has(this.runFilter)
    if (this.hasAutoSortTarget) {
      this.autoSortTarget.textContent = this.runFilter === "all" ? "Auto sort all" : "Auto sort"
      this.autoSortTarget.disabled = !this.sortableRuns().some(run => this.stopPositions.get(run.id).size > 1)
      this.autoSortTarget.title = this.runFilter === "unassigned" ? "Assign shipments to a run before sorting" : "Meet receiving hours first, then optimize your selected preference. Includes all stops, pages, and search results."
    }
    if (this.hasDepotLabelTarget) {
      this.depotLabelTarget.textContent = this.validDepot() ? `Depot: ${this.depotValue.address}` : "Set starting depot"
      this.depotLabelTarget.title = this.depotLabelTarget.textContent
    }
    this.sortStatusTarget.textContent = this.readonlyValue ? "Delivery order" : (this.runFilter === "unassigned" ? "Assign shipments to a run to enable sorting" : "Select a run to reorder stops")
    if (reorder) this.sortStatusTarget.textContent = rows.length > 100 ? "Drag to reorder · Hold over Previous / Next to move pages" : "Drag to reorder stops"
    else if (!this.readonlyValue && this.searchTarget.value.trim()) this.sortStatusTarget.textContent = "Clear search to drag stops"
    if (reorder) this.sortable = Sortable.create(this.listTarget, {
      handle: ".shipment-runs-drag", draggable: ".shipment-runs-item", animation: 150,
      forceFallback: rows.length > 100,
      delay: 150, delayOnTouchOnly: true, ghostClass: "shipment-runs-drag-ghost",
      onStart: event => this.startPageDrag(event, rows),
      onEnd: event => this.finishPageDrag(event)
    })
  }

  startPageDrag(event, rows) {
    this.pageDrag = {
      rows, source: event.item.dataset.stopKey, shipmentId: event.item.dataset.planningShipment,
      runId: this.runFilter, originalPage: this.page, originalScroll: this.listTarget.scrollTop,
      page: this.page, firstPage: this.page, lastPage: this.page
    }
    this.pageDragAbort = new AbortController()
    const options = { signal: this.pageDragAbort.signal, passive: false }
    for (const type of ["dragover", "pointermove", "touchmove"]) {
      document.addEventListener(type, event => this.trackPageDrag(event), options)
    }
    document.addEventListener("keydown", event => {
      if (event.key === "Escape" && this.pageDrag) {
        this.pageDrag.canceled = true
        clearTimeout(this.pageDragTimer)
        this.sortable?.option("disabled", true)
      }
    }, options)
    if (rows.length > 100) {
      this.element.classList.add("shipment-runs-dragging-pages")
      this.sortStatusTarget.textContent = "Hold over Previous / Next to change page, then drop in the list."
    }
  }

  trackPageDrag(event) {
    if (!this.pageDrag || this.pageDrag.canceled) return
    const point = event.touches?.[0] || event
    this.pageDrag.point = { x: point.clientX, y: point.clientY }
    const contains = button => {
      const rect = button.getBoundingClientRect()
      return !button.hidden && !button.disabled && point.clientX >= rect.left && point.clientX <= rect.right && point.clientY >= rect.top && point.clientY <= rect.bottom
    }
    const direction = contains(this.previousTarget) ? -1 : contains(this.nextTarget) ? 1 : null
    if (direction && event.cancelable) event.preventDefault()
    if (direction === this.pageDrag.direction) return
    clearTimeout(this.pageDragTimer)
    this.pageDrag.direction = direction
    this.previousTarget.classList.toggle("is-drag-page-target", direction === -1)
    this.nextTarget.classList.toggle("is-drag-page-target", direction === 1)
    if (direction) this.pageDragTimer = setTimeout(() => this.showDragPage(direction), 600)
  }

  showDragPage(direction) {
    const drag = this.pageDrag
    if (!drag || !this.canReorder() || drag.runId !== this.runFilter) return
    const page = drag.page + direction
    if (page < 0 || page * 100 >= drag.rows.length) return
    // Keep the actual dragged element mounted; replacing the page ends native drags.
    const fragment = document.createDocumentFragment()
    if (page < drag.firstPage) {
      drag.rows.slice(page * 100, drag.firstPage * 100).forEach(row => fragment.append(this.buildShipmentRow(row, true)))
      this.listTarget.prepend(fragment)
      drag.firstPage = page
    } else if (page > drag.lastPage) {
      drag.rows.slice((drag.lastPage + 1) * 100, (page + 1) * 100).forEach(row => fragment.append(this.buildShipmentRow(row, true)))
      this.listTarget.append(fragment)
      drag.lastPage = page
    }
    drag.page = page
    const anchorRow = drag.rows.slice(page * 100, (page + 1) * 100).find(row => String(row.shipment_id) !== drag.shipmentId)
    const anchor = anchorRow && this.listTarget.querySelector(`[data-planning-shipment='${anchorRow.shipment_id}']`)
    if (anchor) this.listTarget.scrollTop += anchor.getBoundingClientRect().top - this.listTarget.getBoundingClientRect().top
    this.pageLabelTarget.textContent = `${page * 100 + 1}–${Math.min((page + 1) * 100, drag.rows.length)} of ${drag.rows.length}`
    this.previousTarget.disabled = page === 0
    this.nextTarget.disabled = (page + 1) * 100 >= drag.rows.length
    this.selectionChanged()
  }

  clearPageDrag() {
    clearTimeout(this.pageDragTimer)
    this.pageDragAbort?.abort()
    this.pageDrag = null
    this.element.classList.remove("shipment-runs-dragging-pages")
    if (this.hasPreviousTarget) this.previousTarget.classList.remove("is-drag-page-target")
    if (this.hasNextTarget) this.nextTarget.classList.remove("is-drag-page-target")
  }

  cancelPageDrag() {
    const drag = this.pageDrag
    if (!drag) return
    this.clearPageDrag()
    this.page = drag.originalPage
    // Let the drag's mouseup/click finish before replacing its source element.
    this.cancelDragFrame = requestAnimationFrame(() => {
      this.renderList()
      this.selectionChanged()
      this.listTarget.scrollTop = drag.originalScroll
    })
  }

  finishPageDrag(event) {
    const drag = this.pageDrag
    if (!drag) return
    const original = event.originalEvent
    const rect = this.listTarget.getBoundingClientRect()
    const outside = drag.point && (drag.point.x < rect.left || drag.point.x > rect.right || drag.point.y < rect.top || drag.point.y > rect.bottom)
    if (drag.canceled || outside || original?.type === "touchcancel" || (original?.type === "dragend" && original.dataTransfer?.dropEffect === "none")) {
      this.cancelPageDrag()
      return
    }
    const items = [...this.listTarget.children].filter(item => item.dataset.planningShipment && item !== event.clone && !item.classList.contains("sortable-fallback"))
    const index = items.indexOf(event.item)
    const next = items.slice(index + 1).find(item => item.dataset.stopKey !== drag.source)
    const previous = items.slice(0, index).reverse().find(item => item.dataset.stopKey !== drag.source)
    this.clearPageDrag()
    if (index < 0 || drag.runId !== this.runFilter || (!next && !previous)) { this.refreshWorkspace(); return }
    this.reorderStop(drag.source, (next || previous).dataset.stopKey, !next)
    this.followMovedStop(drag.shipmentId)
  }

  buildShipmentRow(row, reorder) {
    const id = String(row.shipment_id)
    const position = this.runLookup.has(this.runFilter) ? this.stopPositions.get(this.runFilter)?.get(row.stop_key) : null
    const expanded = this.expandedShipmentIds.has(id)
    const item = document.createElement("div")
    item.className = "shipment-runs-item"
    item.classList.toggle("is-expanded", expanded)
    item.style.borderLeftColor = this.runLookup.get(this.proposals[id])?.color || "#6c757d"
    item.dataset.planningShipment = id
    item.dataset.stopKey = row.stop_key
    const heading = document.createElement("div")
    heading.className = "shipment-runs-item-heading"
    if (reorder) {
      const handle = document.createElement("button")
      handle.type = "button"
      handle.className = "shipment-runs-drag"
      handle.textContent = "⠿"
      handle.setAttribute("aria-label", `Move stop for ${row.number}`)
      handle.title = "Drag this stop. Hold over Previous/Next to move between pages. Arrow keys move one stop; Home/End moves first/last."
      handle.addEventListener("keydown", event => this.moveStopWithKeyboard(event, row))
      heading.append(handle)
    }
    if (!this.readonlyValue) {
      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.className = "check flex-shrink-0"
      checkbox.dataset.planSelection = id
      checkbox.setAttribute("aria-label", `Select ${row.number}`)
      checkbox.addEventListener("change", () => { checkbox.checked ? this.selected.add(id) : this.selected.delete(id); this.selectionChanged() })
      heading.append(checkbox)
    }
    if (position != null) {
      const number = document.createElement("span")
      number.className = "shipment-runs-stop-index"
      number.textContent = position + 1
      number.setAttribute("aria-label", `Stop ${position + 1}`)
      number.title = `Stop ${position + 1}`
      heading.append(number)
    }
    const customer = document.createElement(row.position ? "button" : "span")
    customer.className = "shipment-runs-customer"
    customer.textContent = row.client
    customer.title = row.client
    if (row.position) {
      customer.type = "button"
      customer.setAttribute("aria-label", `Show on map: ${row.number} · ${row.client}`)
      customer.dataset.shipmentId = id
      customer.addEventListener("click", event => this.focusShipment(event))
    }
    heading.append(customer)
    const detailsButton = document.createElement("button")
    detailsButton.type = "button"
    detailsButton.className = "shipment-runs-details-toggle"
    detailsButton.textContent = "Details"
    detailsButton.setAttribute("aria-label", `Details for ${row.number}`)
    detailsButton.setAttribute("aria-expanded", String(expanded))
    detailsButton.setAttribute("aria-controls", `shipment-run-details-${id}`)
    heading.append(detailsButton)
    item.append(heading)
    const meta = document.createElement("div")
    meta.className = "shipment-runs-item-meta"
    const reference = document.createElement("span")
    reference.textContent = `${row.number} · ${row.status}`
    meta.append(reference)
    if (!this.runLookup.has(this.runFilter)) {
      const run = document.createElement("span")
      run.className = "shipment-runs-run-label"
      run.textContent = this.runLookup.get(this.proposals[id])?.name || "Unassigned"
      run.title = run.textContent
      meta.append(run)
    }
    this.addText(meta, "Shown on map", "shipment-runs-map-focus")
    item.append(meta)
    const address = document.createElement("div")
    address.className = "shipment-runs-address small fg-2"
    address.textContent = row.address || row.location || "No delivery address"
    address.title = `${row.location} · ${row.address || "No delivery address"}`
    item.append(address)
    const timing = document.createElement("div")
    timing.className = "shipment-runs-stop-timing"
    item.append(timing)
    if (!row.position) this.addText(item, "No map pin", "shipment-runs-row-warning")
    if (row.location_warning) this.addText(item, row.location_warning, "shipment-runs-row-warning")
    const details = document.createElement("div")
    details.id = `shipment-run-details-${id}`
    details.className = "shipment-runs-item-details"
    details.hidden = !expanded
    detailsButton.addEventListener("click", () => {
      const open = details.hidden
      details.hidden = !open
      detailsButton.setAttribute("aria-expanded", String(open))
      item.classList.toggle("is-expanded", open)
      open ? this.expandedShipmentIds.add(id) : this.expandedShipmentIds.delete(id)
    })
    const definitions = document.createElement("dl")
    definitions.className = "shipment-runs-detail-fields"
    const fields = [["Order", row.order_number], ["Location", row.location], ["Address", row.address], ["Default run", row.default_run], ["Execution", row.assigned_run]]
    fields.filter(([, value]) => value).forEach(([label, value]) => {
      const term = document.createElement("dt"), definition = document.createElement("dd")
      term.textContent = label
      definition.textContent = value
      definitions.append(term, definition)
    })
    details.append(definitions)
    if (!this.readonlyValue) {
      const label = document.createElement("label")
      label.className = "form-label small mb-1"
      label.textContent = "Proposed run"
      label.htmlFor = `shipment-proposed-run-${id}`
      const select = document.createElement("select")
      select.id = label.htmlFor
      select.className = "form-control form-control-sm"
      select.setAttribute("aria-label", `Proposed run for ${row.number}`)
      this.populateRuns(select, this.proposals[id])
      select.addEventListener("change", () => this.move([id], select.value))
      details.append(label, select)
    }
    const timingDetails = document.createElement("div")
    timingDetails.className = "shipment-runs-timing-details"
    details.append(timingDetails)
    item.append(details)
    this.renderStopTiming(timing, row)
    return item
  }

  sortableRuns() { return this.runs.filter(run => this.runFilter === "all" || run.id === this.runFilter) }

  orderedStopsFor(runId) {
    const stops = new Map()
    this.rows.filter(row => this.proposals[String(row.shipment_id)] === runId).forEach(row => {
      const window = { windowStart: row.delivery_window_start, windowEnd: row.delivery_window_end }
      if (!stops.has(row.stop_key)) stops.set(row.stop_key, { key: row.stop_key, position: row.position, ...window, windows: [window], serviceMinutes: row.service_time_minutes ?? 5 })
      else {
        const stop = stops.get(row.stop_key)
        if (JSON.stringify(stop.position) !== JSON.stringify(row.position)) stop.position = null
        stop.serviceMinutes = Math.max(stop.serviceMinutes, row.service_time_minutes ?? 5)
        if (!stop.windows.some(existing => JSON.stringify(existing) === JSON.stringify(window))) stop.windows.push(window)
      }
    })
    return [...this.stopPositions.get(runId).keys()].map(key => stops.get(key))
  }

  settingsFor(runId) { return this.runSettings[runId] || { start_time: "", method: "finish" } }
  timedRuns() { return this.sortableRuns().filter(run => this.stopPositions.get(run.id)?.size) }
  timingContext(runId) { return scheduleContext({ startTime: this.settingsFor(runId).start_time, date: this.dateValue, timeZone: this.timeZoneValue }) }
  timeLabel(minutes) { return formatRunTime(minutes, { date: this.dateValue, timeZone: this.timeZoneValue }) }

  renderTimingControls() {
    this.runSettingsFieldTarget.value = JSON.stringify(this.runSettings)
    if (!this.hasTimingControlsTarget) { this.renderTimingSummary(); return }
    const runs = this.timedRuns(), times = new Set(runs.map(run => this.settingsFor(run.id).start_time)), methods = new Set(runs.map(run => this.settingsFor(run.id).method))
    this.timingControlsTarget.hidden = !runs.length
    this.planOptionsTarget.hidden = !runs.length
    this.departureLabelTarget.textContent = this.runFilter === "all" ? (times.size > 1 ? "Departure (varies by run)" : "Departure (all runs)") : "Departure time"
    this.departureTarget.value = times.size === 1 ? [...times][0] : ""
    this.departureTarget.title = times.size > 1 ? "Runs have different departure times. Enter a time here to update all runs." : `Departure in ${this.timeZoneValue}`
    this.sortMethodTarget.querySelector('option[value=""]').hidden = methods.size <= 1
    this.sortMethodTarget.value = methods.size === 1 ? [...methods][0] : ""
    this.planOptionsTarget.querySelector("summary").textContent = `Run settings · ${this.sortMethodTarget.selectedOptions[0].textContent}`
    this.applyTimeAllTarget.hidden = !this.runLookup.has(this.runFilter) || !this.departureTarget.value
    this.renderTimingSummary()
  }

  changeDeparture() {
    if (this.readonlyValue || this.sorting) return
    const value = this.departureTarget.value
    this.timedRuns().forEach(run => { this.runSettings[run.id] = { ...this.settingsFor(run.id), start_time: value } })
    this.markDirty()
    this.refreshWorkspace()
  }

  applyTimeToAll() {
    if (this.readonlyValue || this.sorting || !this.departureTarget.value) return
    const value = this.departureTarget.value
    this.runs.forEach(run => { this.runSettings[run.id] = { ...this.settingsFor(run.id), start_time: value } })
    this.markDirty()
    this.refreshWorkspace()
  }

  changeSortMethod() {
    if (this.readonlyValue || this.sorting || !this.sortMethodTarget.value) return
    this.timedRuns().forEach(run => { this.runSettings[run.id] = { ...this.settingsFor(run.id), method: this.sortMethodTarget.value } })
    this.markDirty()
    this.refreshWorkspace()
  }

  refreshEstimates() {
    if (!this.estimates) return
    this.runs.forEach(run => {
      const stops = this.orderedStopsFor(run.id), start = this.settingsFor(run.id).start_time
      const signature = JSON.stringify([this.depotValue, start, stops, !!window.google?.maps?.importLibrary])
      const old = this.estimates.get(run.id)
      if (old?.signature === signature) return
      clearTimeout(old?.timer)
      old?.abort?.abort()
      const state = { signature, status: !stops.length ? "empty" : !start ? "missing" : !this.validDepot() ? "depot" : "pending" }
      this.estimates.set(run.id, state)
      if (state.status !== "pending") return
      try { state.context = this.timingContext(run.id) } catch (error) { state.status = "error"; state.message = error.message; return }
      if (!window.google?.maps?.importLibrary) { state.status = "error"; state.message = "Routing unavailable. Retry when Google Maps is available."; return }
      state.abort = new AbortController()
      state.timer = setTimeout(async () => {
        try {
          const missing = stops.findIndex(stop => !routePoint(stop.position))
          const known = missing < 0 ? stops : stops.slice(0, missing)
          const route = await this.roadRoutes.get(this.depotValue, known, { signal: state.abort.signal })
          if (this.estimates.get(run.id) !== state) return
          state.schedule = scheduleRun(stops, { context: state.context, legs: route.legs })
          state.visits = new Map(state.schedule.visits.map(visit => [visit.key, visit]))
          state.status = "ready"
        } catch (error) {
          if (error.name === "AbortError" || this.estimates.get(run.id) !== state) return
          state.status = "error"
          state.message = error.userFacing ? error.message : routeErrorMessage(error)
        }
        this.renderTimingSummary()
        this.listTarget.querySelectorAll("[data-planning-shipment]").forEach(item => {
          const row = this.rows.find(row => String(row.shipment_id) === item.dataset.planningShipment)
          this.renderStopTiming(item.querySelector(".shipment-runs-stop-timing"), row)
        })
      }, 350)
    })
    this.renderTimingSummary()
  }

  scheduleSummary(schedule) {
    if (schedule.incomplete) return `${schedule.incomplete} stop estimates unavailable; check map pins or road travel times`
    return `Finish ${this.timeLabel(schedule.finish)} · ${(schedule.distance / 1000).toFixed(1)} km · ${Math.ceil(schedule.driving)} min driving · ${Math.ceil(schedule.waiting)} min waiting · ${Math.ceil(schedule.service)} min unloading · ${schedule.lateStops} receiving-hour ${schedule.lateStops === 1 ? "warning" : "warnings"}`
  }

  renderTimingSummary() {
    if (!this.hasTimingSummaryTarget) return
    this.timingSummaryTarget.replaceChildren()
    if (this.partialValue) this.addText(this.timingSummaryTarget, "Source filter active: estimates cover stops in this preview only.", "shipment-runs-estimate-notice")
    const runs = this.timedRuns(), ready = []
    const allMissing = runs.length && runs.every(run => this.estimates.get(run.id)?.status === "missing")
    if (allMissing) this.addText(this.timingSummaryTarget, this.readonlyValue ? "Departure time not specified" : "Set a departure time to see estimates", "fg-2")
    let retry = false
    runs.forEach(run => {
      const state = this.estimates.get(run.id)
      if (!state || allMissing) return
      const labels = { missing: "Set a departure time to see estimates", depot: "Set a starting depot to see estimates", pending: "Calculating stop estimates…" }
      const summary = document.createElement("div")
      summary.className = "shipment-runs-run-estimate"
      if (runs.length > 1) this.addText(summary, run.name, "shipment-runs-estimate-name")
      if (state.status === "ready") {
        ready.push({ run, state })
        const text = state.schedule.incomplete ? this.scheduleSummary(state.schedule) : `Finish ${this.timeLabel(state.schedule.finish)} · ${(state.schedule.distance / 1000).toFixed(1)} km`
        this.addText(summary, text, state.schedule.incomplete ? "shipment-runs-estimate-notice" : "shipment-runs-estimate-total")
        if (!state.schedule.incomplete && !state.schedule.lateStops) this.addText(summary, "No late stops", "shipment-runs-estimate-clear fg-2")
      } else this.addText(summary, state.message || labels[state.status], state.status === "error" ? "shipment-runs-estimate-notice" : "fg-2")
      if (state.schedule?.lateStops) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "shipment-runs-estimate-warning"
        button.textContent = `${state.schedule.lateStops} ${state.schedule.lateStops === 1 ? "late stop" : "late stops"}`
        button.setAttribute("aria-label", `Review receiving-hour warnings for ${run.name}`)
        button.addEventListener("click", () => this.reviewTimingWarnings(run.id))
        summary.append(button)
      }
      this.timingSummaryTarget.append(summary)
      retry ||= state.status === "error"
    })
    if (ready.length) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "shipment-runs-travel-trigger"
      button.textContent = "Travel breakdown"
      button.dataset.bsToggle = "dialog"
      button.dataset.bsTarget = "#shipment-runs-travel-dialog"
      button.setAttribute("aria-haspopup", "dialog")
      button.setAttribute("aria-controls", "shipment-runs-travel-dialog")
      button.addEventListener("click", () => this.renderTravelBreakdown())
      this.timingSummaryTarget.append(button)
    }
    this.retryEstimatesTarget.hidden = !retry
    if (this.hasTravelDialogTarget && this.travelDialogTarget.open) this.renderTravelBreakdown()
  }

  renderTravelBreakdown() {
    this.travelBodyTarget.replaceChildren()
    if (this.partialValue) this.addText(this.travelBodyTarget, "Shipment source filter active. These figures cover stops in this preview only.", "shipment-runs-estimate-notice mb-3")
    const runs = this.timedRuns()
    if (!runs.length) this.addText(this.travelBodyTarget, "No assigned runs in this view.", "fg-2")
    runs.forEach(run => {
      const state = this.estimates.get(run.id), schedule = state?.schedule
      const card = document.createElement("section")
      card.className = "shipment-runs-travel-card"
      card.dataset.travelRun = run.id
      card.setAttribute("aria-labelledby", `travel-run-title-${run.id}`)
      const header = document.createElement("div")
      header.className = "shipment-runs-travel-card-heading"
      const title = document.createElement("h3")
      title.id = `travel-run-title-${run.id}`
      const dot = document.createElement("span")
      dot.className = "shipment-runs-travel-run-color"
      dot.style.backgroundColor = run.color
      dot.setAttribute("aria-hidden", "true")
      title.append(dot, document.createTextNode(run.name))
      header.append(title)
      if (schedule?.lateStops) {
        const review = document.createElement("button")
        review.type = "button"
        review.className = "shipment-runs-estimate-warning"
        review.textContent = `${schedule.lateStops} ${schedule.lateStops === 1 ? "late stop" : "late stops"} · Review`
        review.setAttribute("aria-label", `Review receiving-hour warnings for ${run.name}`)
        review.disabled = !!this.sorting
        review.addEventListener("click", async () => {
          await window.bootstrap.Dialog.getInstance(this.travelDialogTarget).hide()
          if (this.element.isConnected) this.reviewTimingWarnings(run.id)
        })
        header.append(review)
      } else if (schedule && !schedule.incomplete) this.addText(header, "No late stops", "small fg-2")
      card.append(header)
      if (!schedule) {
        const labels = { missing: "Departure time not specified.", depot: "Set a starting depot to calculate estimates.", pending: "Calculating estimates…", error: "Estimates unavailable. Close this view and retry estimates." }
        this.addText(card, state?.message || labels[state?.status] || "Estimates unavailable.", "shipment-runs-estimate-notice")
      } else {
        if (schedule.incomplete) {
          const known = schedule.visits.length - schedule.incomplete
          this.addText(card, `${schedule.incomplete} ${schedule.incomplete === 1 ? "stop cannot" : "stops cannot"} be estimated. Distance and durations cover the ${known} estimated ${known === 1 ? "stop" : "stops"} only.`, "shipment-runs-estimate-notice mb-3")
        }
        const duration = minutes => {
          const rounded = Math.ceil(minutes)
          return rounded >= 60 ? `${Math.floor(rounded / 60)} hr${rounded % 60 ? ` ${rounded % 60} min` : ""}` : `${rounded} min`
        }
        const knownLabel = label => schedule.incomplete ? `Known ${label.toLowerCase()}` : label
        const metrics = [["Departure", this.timeLabel(schedule.start)], ["Estimated finish", this.timeLabel(schedule.finish)],
          [knownLabel("Distance"), `${(schedule.distance / 1000).toFixed(1)} km`], [knownLabel("Driving"), duration(schedule.driving)],
          [knownLabel("Waiting"), duration(schedule.waiting)], [knownLabel("Unloading"), duration(schedule.service)]]
        const values = document.createElement("dl")
        values.className = "shipment-runs-travel-metrics"
        metrics.forEach(([label, value]) => {
          const metric = document.createElement("div"), term = document.createElement("dt"), definition = document.createElement("dd")
          term.textContent = label
          definition.textContent = value
          metric.append(term, definition)
          values.append(metric)
        })
        card.append(values)
      }
      this.travelBodyTarget.append(card)
    })
  }

  renderStopTiming(element, row) {
    if (!element || !row) return
    element.replaceChildren()
    const details = element.closest(".shipment-runs-item")?.querySelector(".shipment-runs-timing-details")
    details?.replaceChildren()
    const stop = { windowStart: row.delivery_window_start, windowEnd: row.delivery_window_end }
    const state = this.estimates.get(this.proposals[String(row.shipment_id)]), visit = state?.visits?.get(row.stop_key)
    const metrics = document.createElement("div")
    metrics.className = "shipment-runs-stop-metrics"
    if (visit && !visit.unknown) this.addText(metrics, `Arrive ${this.timeLabel(visit.arrival)}`, "shipment-runs-arrival")
    if (stop.windowStart || stop.windowEnd) this.addText(metrics, `Receiving: ${receivingHours(stop)}`, "fg-2")
    if (metrics.childElementCount) element.append(metrics)
    if (!visit) {
      if (details) this.addText(details, `${row.service_time_minutes ?? 5} min unloading per stop`, "small fg-2")
      return
    }
    if (visit.unknown) { this.addText(element, "ETA unavailable: missing pin or travel time at this or an earlier stop", "shipment-runs-row-warning"); return }
    if (details) this.addText(details, `Arrive ${this.timeLabel(visit.arrival)} · Leave ${this.timeLabel(visit.departure)} · ${visit.service} min unloading`, "small fg-2")
    if (visit.waiting > 0) this.addText(element, `Wait ${Math.ceil(visit.waiting)} min · Opens ${this.timeLabel(visit.begins)}`, "shipment-runs-timing-warning theme-warning")
    if (visit.late > 1 / 60) this.addText(element, visit.arrivalLate > 1 / 60 ? `Arrive ${Math.ceil(visit.arrivalLate)} min after receiving closes` : `Unloading finishes ${Math.ceil(visit.late)} min after receiving closes`, "shipment-runs-timing-warning theme-danger")
  }

  reviewTimingWarnings(runId) {
    if (this.sorting) return
    const visit = this.estimates.get(runId)?.schedule?.visits.find(visit => visit.late > 1 / 60)
    const row = this.rows.find(row => this.proposals[String(row.shipment_id)] === runId && row.stop_key === visit?.key)
    if (!row) return
    this.runFilter = runId
    this.selected.clear()
    this.searchTarget.value = ""
    this.page = Math.max(0, Math.floor(this.filteredRows().findIndex(candidate => candidate.shipment_id === row.shipment_id) / 100))
    this.refreshWorkspace()
    this.showView("list")
    this.listTarget.querySelector(`[data-planning-shipment='${row.shipment_id}']`)?.scrollIntoView({ block: "center" })
  }

  retryEstimates() {
    this.estimates.forEach(state => { clearTimeout(state.timer); state.abort?.abort() })
    this.estimates.clear()
    this.refreshWorkspace()
  }

  toggleRoutes() {
    if (this.sorting) return
    if (!this.routesShown && !this.validDepot() && !this.readonlyValue) {
      this.openDepot(false)
      this.routesAfterDepotSave = true
      this.saveDepotTarget.textContent = "Save depot & show routes"
      return
    }
    this.routesShown = !this.routesShown
    if (this.routesShown) { this.searchTarget.value = ""; this.page = 0; this.showView("map") }
    this.refreshWorkspace()
  }

  clearRoadRoutes() {
    this.routesAbort?.abort()
    clearTimeout(this.routesTimer)
    this.routeLines?.forEach(line => line.setMap(null))
    this.routeLines = []
    this.routePaths = []
    this.routeSignature = null
  }

  refreshRoadRoutes() {
    if (!this.hasRoutesButtonTarget) return
    this.routesButtonTarget.textContent = this.routesShown ? "Hide routes" : "Show routes"
    this.routesButtonTarget.setAttribute("aria-pressed", String(this.routesShown))
    this.routesPanelTarget.hidden = !this.routesShown
    if (!this.routesShown) { this.clearRoadRoutes(); return }
    const runs = this.sortableRuns().map(run => ({ ...run, stops: this.orderedStopsFor(run.id) })).filter(run => run.stops.length)
    const unavailable = !runs.length ? "Choose an assigned run to show its road route." :
      !this.validDepot() ? "Set a starting depot before showing road routes." :
      this.mapFailed || window.googleMapsAuthFailed ? "Map unavailable. You can still plan from the list." :
      !this.mapInitialized ? "Waiting for Google Maps…" : null
    if (unavailable) {
      this.clearRoadRoutes()
      this.routesSummaryTarget.textContent = unavailable
      this.retryRoutesTarget.hidden = true
      return
    }
    // Search and pagination never truncate the driving route or trigger a billable request.
    const signature = JSON.stringify([routePoint(this.depotValue), runs.map(run => [run.id, run.stops])])
    if (signature === this.routeSignature) return
    this.clearRoadRoutes()
    this.routeSignature = signature
    this.routesSummaryTarget.textContent = "Drawing road routes…"
    this.retryRoutesTarget.hidden = true
    const abort = new AbortController()
    this.routesAbort = abort
    this.routesTimer = setTimeout(() => this.drawRoadRoutes(runs, abort.signal), 350)
  }

  async drawRoadRoutes(runs, signal) {
    this.routesSummaryTarget.replaceChildren()
    for (const run of runs) {
      if (signal.aborted) return
      const row = document.createElement("div")
      row.className = "shipment-runs-route-row"
      row.style.borderLeftColor = run.color
      row.dataset.routeRun = run.id
      this.addText(row, `${run.name} · ${run.stops.length} ${run.stops.length === 1 ? "stop" : "stops"}`, "fw-semibold")
      const status = document.createElement("div")
      status.className = "small fg-2"
      row.append(status)
      this.routesSummaryTarget.append(row)
      const missing = run.stops.filter(stop => !routePoint(stop.position)).length
      if (missing) {
        status.textContent = `${missing} ${missing === 1 ? "stop needs" : "stops need"} a map pin. Route not drawn.`
        continue
      }
      status.textContent = "Finding road route…"
      const lineStart = this.routeLines.length
      const pathStart = this.routePaths.length
      try {
        const result = await this.roadRoutes.get(this.depotValue, run.stops, {
          signal, progress: (done, total) => { if (!signal.aborted) status.textContent = `Drawing section ${done} of ${total}…` }
        })
        if (signal.aborted) return
        result.segments.forEach(segment => {
          const lines = segment.createPolylines({ polylineOptions: {
            strokeColor: run.color, strokeWeight: 4, strokeOpacity: 0.85, clickable: false,
            icons: [{ icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: "80px", repeat: "160px" }]
          } })
          this.routeLines.push(...lines)
          lines.forEach(line => line.setMap(this.map()))
          this.routePaths.push(...segment.path)
        })
        const distance = result.distanceMeters == null ? "Distance unavailable" : `${(result.distanceMeters / 1000).toFixed(1)} km`
        const minutes = result.durationMillis == null ? null : Math.ceil(result.durationMillis / 60000)
        const duration = minutes == null ? "Time unavailable" : minutes >= 60 ? `${Math.floor(minutes / 60)} hr ${minutes % 60} min driving` : `${minutes} min driving`
        status.textContent = `${distance} · ${duration}`
        this.fitLocations()
      } catch (error) {
        if (signal.aborted) return
        this.routeLines.splice(lineStart).forEach(line => line.setMap(null))
        this.routePaths.splice(pathStart)
        status.textContent = routeErrorMessage(error)
        status.classList.replace("fg-2", "fg-danger")
        if (/Enable Routes API|Routes API is disabled/.test(status.textContent)) {
          const setup = document.createElement("a")
          setup.href = routeSetupUrl(error)
          setup.target = "_blank"
          setup.rel = "noopener"
          setup.className = "small"
          setup.textContent = "Open Google API settings"
          row.append(setup)
        }
        this.retryRoutesTarget.hidden = false
        // A missing drivable route is local to one run. Provider failures stop the
        // batch so a disabled API or quota limit doesn't cause repeated requests.
        if (!/ZERO_RESULTS|NOT_FOUND/.test(`${error.code} ${error.message}`)) {
          if (run !== runs.at(-1)) this.addText(this.routesSummaryTarget, "Remaining routes were not drawn. Retry when routing is available.", "small fg-2")
          return
        }
      }
    }
  }

  retryRoadRoutes() { this.routeSignature = null; this.refreshRoadRoutes() }

  fitLocations() {
    if (!this.routePaths?.length) { super.fitLocations(); return }
    if (!this._map || this.mapTarget.hidden || !this.mapTarget.clientWidth) return
    const bounds = new google.maps.LatLngBounds()
    this.markers().forEach(marker => bounds.extend(marker.getPosition()))
    this.routePaths.forEach(point => bounds.extend({ lat: typeof point.lat === "function" ? point.lat() : point.lat, lng: typeof point.lng === "function" ? point.lng() : point.lng }))
    this.map().fitBounds(bounds, 48)
    this.fittedVisible = true
  }

  async autoSort() {
    if (this.readonlyValue || this.sorting) return
    const runs = this.timedRuns()
    if (!runs.length) return
    if (!this.validDepot()) { this.openDepot(true); return }
    const missing = runs.filter(run => clockMinutes(this.settingsFor(run.id).start_time) == null)
    if (missing.length) {
      this.sortResultTarget.textContent = `Set a departure time for ${missing.map(run => run.name).join(", ")} before auto sorting.`
      this.sortResultTarget.hidden = false
      this.departureTarget.focus()
      return
    }
    if (!window.google?.maps?.importLibrary) {
      this.sortResultTarget.textContent = "Road routing is unavailable. Retry when Google Maps is available; you can still adjust stops manually."
      this.sortResultTarget.hidden = false
      return
    }
    const undo = { orders: structuredClone(this.stopOrders), dirty: this.dirty, label: this.dirtyTarget.textContent }
    const controls = [...this.element.querySelectorAll("button, input, select")].filter(control => !control.disabled && control !== this.cancelSortTarget)
    controls.forEach(control => { control.disabled = true })
    this.sorting = true
    this.sortAbort = new AbortController()
    this.sortable?.option("disabled", true)
    this.mapTarget.inert = true
    this.element.setAttribute("aria-busy", "true")
    this.autoSortTarget.textContent = "Calculating…"
    this.cancelSortTarget.hidden = false
    this.sortResultTarget.textContent = "Checking receiving hours and road travel times…"
    this.sortResultTarget.hidden = false
    const results = []
    try {
      for (const run of runs) {
        const stops = this.orderedStopsFor(run.id)
        const result = await optimizeShipmentRun(stops, {
          depot: this.depotValue, context: this.timingContext(run.id), method: this.settingsFor(run.id).method,
          getRoute: (order, options) => this.roadRoutes.get(this.depotValue, order, options), signal: this.sortAbort.signal,
          progress: (done, total) => { this.sortResultTarget.textContent = `${run.name}: checking road route ${done} of ${total}…` }
        })
        results.push({ run, ...result, changed: stops.some((stop, index) => stop.key !== result.keys[index]) })
      }
      this.pendingSort = { results, undo }
      if (results.some(result => result.changed)) {
        this.sortBodyTarget.replaceChildren()
        results.forEach(result => {
          const item = document.createElement("div")
          item.className = "border-top py-3"
          this.addText(item, `${result.run.name} · ${this.settingsFor(result.run.id).start_time} departure`, "fw-semibold")
          this.addText(item, `Current: ${this.scheduleSummary(result.before)}`, "small fg-2 mt-2")
          this.addText(item, `Proposed: ${this.scheduleSummary(result.after)}`, "small mt-2")
          if (result.after.lateStops) this.addText(item, `${result.after.lateStops} stops still conflict with receiving hours. Review these warnings before dispatch.`, "small fg-warning mt-2")
          if (!result.changed) this.addText(item, "Current order kept.", "small fg-2")
          this.sortBodyTarget.append(item)
        })
        window.bootstrap.Dialog.getOrCreateInstance(this.sortDialogTarget).show()
        this.sortResultTarget.textContent = "Review the proposed order before applying."
      } else {
        this.sortResultTarget.textContent = "No better order found. Current stop order kept; review any receiving-hour warnings."
      }
    } catch (error) {
      this.pendingSort = null
      this.sortResultTarget.textContent = error.name === "AbortError" ? "Calculation canceled. Current stop order kept." : `${error.userFacing ? error.message : routeErrorMessage(error)} Current stop order kept.`
    } finally {
      this.sorting = false
      this.cancelSortTarget.hidden = true
      controls.forEach(control => { control.disabled = false })
      this.element.removeAttribute("aria-busy")
      this.mapTarget.inert = false
      this.refreshWorkspace()
    }
  }

  cancelAutoSort() { this.sortAbort?.abort() }

  applyAutoSort() {
    if (this.readonlyValue || this.sorting || !this.pendingSort) return
    const { results, undo } = this.pendingSort
    this.markDirty()
    results.filter(result => result.changed).forEach(result => { this.stopOrders[result.run.id] = result.keys })
    this.sortUndo = undo
    this.undoSortTarget.hidden = false
    this.pendingSort = null
    window.bootstrap.Dialog.getInstance(this.sortDialogTarget)?.hide()
    this.page = 0
    this.searchTarget.value = ""
    this.selected.clear()
    this.refreshWorkspace()
    const changed = results.filter(result => result.changed).length
    this.sortResultTarget.textContent = `Auto sorted ${changed} ${changed === 1 ? "run" : "runs"}. Receiving hours prioritized; review remaining warnings.`
    this.sortResultTarget.hidden = false
    this.listTarget.scrollTop = 0
    this.fitLocations()
  }

  undoAutoSort() {
    if (this.readonlyValue || this.sorting || !this.sortUndo) return
    this.stopOrders = this.sortUndo.orders
    this.dirty = this.sortUndo.dirty
    this.dirtyTarget.textContent = this.sortUndo.label
    this.dirtyTarget.classList.toggle("is-dirty", !!this.dirty)
    this.clearSortResult()
    this.page = 0
    this.refreshWorkspace()
    this.sortResultTarget.textContent = "Auto sort undone. Previous stop order restored."
    this.sortResultTarget.hidden = false
  }

  clearSortResult() {
    this.sortUndo = null
    if (this.hasUndoSortTarget) this.undoSortTarget.hidden = true
    if (this.hasSortResultTarget) this.sortResultTarget.hidden = true
  }

  validDepot() {
    const depot = this.depotValue
    return !!depot.address && Number.isFinite(depot.latitude) && Math.abs(depot.latitude) <= 90 && Number.isFinite(depot.longitude) && Math.abs(depot.longitude) <= 180
  }

  editDepot() { this.openDepot(false) }

  openDepot(sortAfterSave) {
    if (this.readonlyValue || this.sorting) return
    this.sortAfterDepotSave = sortAfterSave
    this.routesAfterDepotSave = false
    this.depotErrorTarget.textContent = ""
    this.saveDepotTarget.textContent = sortAfterSave ? "Save depot & auto sort" : "Save depot"
    window.bootstrap.Dialog.getOrCreateInstance(this.depotDialogTarget).show()
  }

  async saveDepot(event) {
    event.preventDefault()
    if (this.readonlyValue || this.savingDepot) return
    this.savingDepot = true
    this.depotAbort = new AbortController()
    this.saveDepotTarget.disabled = true
    this.depotErrorTarget.textContent = ""
    try {
      const response = await fetch(this.depotUrlValue, {
        method: "PATCH", credentials: "same-origin", signal: this.depotAbort.signal,
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: new FormData(event.target)
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || "Could not save the depot. Try again.")
      this.depotValue = result
      this.clearSortResult()
      const sortAfterSave = this.sortAfterDepotSave && this.depotDialogTarget.open
      if (this.routesAfterDepotSave && this.depotDialogTarget.open) {
        this.routesShown = true
        this.searchTarget.value = ""
        this.page = 0
      }
      window.bootstrap.Dialog.getInstance(this.depotDialogTarget)?.hide()
      this.refreshWorkspace()
      if (sortAfterSave) await this.autoSort()
    } catch (error) {
      if (error.name !== "AbortError") this.depotErrorTarget.textContent = error instanceof SyntaxError ? "Could not save the depot. Reload the page and try again." : error.message
    } finally {
      this.savingDepot = false
      this.saveDepotTarget.disabled = false
    }
  }

  resetStopOrder() {
    if (this.readonlyValue || this.sorting || !this.runLookup.has(this.runFilter)) return
    this.stopOrders[this.runFilter] = this.defaultStopKeys(this.runFilter)
    this.page = 0
    this.markDirty()
    this.refreshWorkspace()
  }

  reorderStop(source, target, after = false) {
    if (!this.canReorder()) return
    if (source === target) { this.refreshWorkspace(); return }
    const current = [...this.stopPositions.get(this.runFilter).keys()]
    const keys = current.filter(key => key !== source)
    const index = keys.indexOf(target)
    if (index === -1) return
    keys.splice(index + Number(after), 0, source)
    if (keys.every((key, index) => key === current[index])) { this.refreshWorkspace(); return }
    this.stopOrders[this.runFilter] = keys
    this.markDirty()
    this.refreshWorkspace()
  }

  moveStopWithKeyboard(event, row) {
    if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
    if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || !this.canReorder()) return
    event.preventDefault()
    const keys = [...this.stopPositions.get(this.runFilter).keys()]
    const index = keys.indexOf(row.stop_key)
    const offset = event.key === "ArrowUp" ? -1 : 1
    const targetIndex = event.key === "Home" ? 0 : event.key === "End" ? keys.length - 1 : index + offset
    if (targetIndex < 0 || targetIndex >= keys.length) return
    this.reorderStop(row.stop_key, keys[targetIndex], targetIndex > index)
    this.followMovedStop(row.shipment_id)
  }

  followMovedStop(shipmentId) {
    const rowIndex = this.filteredRows().findIndex(candidate => String(candidate.shipment_id) === String(shipmentId))
    this.page = Math.max(0, Math.floor(rowIndex / 100))
    this.renderList()
    this.selectionChanged()
    this.listTarget.querySelector(`[data-planning-shipment='${shipmentId}'] .shipment-runs-drag`)?.focus()
  }

  populateRuns(select, value = "") {
    const bulk = this.hasBulkRunTarget && select === this.bulkRunTarget
    const options = [...(bulk ? [{ id: "__defaults__", name: "Use default runs" }] : []), { id: "", name: "Unassigned" }, ...this.runs]
    options.forEach(run => {
      const option = document.createElement("option")
      option.value = run.id
      option.textContent = run.name + (run.issue ? " — needs attention" : "")
      select.append(option)
    })
    select.value = bulk ? "__defaults__" : value || ""
  }

  markerOptionsFor(markerData, selected = false) {
    if (markerData.depot) return depotMapMarker()
    if (!markerData.stop_numbers?.length) return { icon: this.buildMarkerIcon(markerData.color, selected), label: null }
    const prefix = markerData.stop_numbers.length === 1 ? "Stop" : "Stops"
    return { ...numberedMapMarker(markerData.color, markerData.stop_numbers, selected), title: `${prefix} ${markerData.stop_numbers.join(", ")} · ${markerData.title}` }
  }

  buildInfoWindowContent(marker) {
    const wrapper = document.createElement("div")
    wrapper.style.cssText = "min-width:16rem;max-width:22rem;max-height:300px;overflow:auto"
    if (marker.depot) {
      this.addText(wrapper, "Starting depot", "fw-semibold")
      this.addText(wrapper, marker.address, "small")
      const close = document.createElement("button")
      close.type = "button"
      close.className = "btn btn-sm btn-link"
      close.textContent = "Close"
      close.addEventListener("click", () => this.clearMarkerFocus())
      wrapper.append(close)
      return wrapper
    }
    const heading = document.createElement("div")
    heading.className = "d-flex justify-content-between align-items-center mb-2"
    heading.style.cssText = "position:sticky;top:0;background:var(--bs-bg-body,white);z-index:1"
    this.addText(heading, `${marker.shipments.length} ${marker.shipments.length === 1 ? "shipment" : "shipments"} at this location`, "fw-semibold")
    const close = document.createElement("button")
    close.type = "button"
    close.textContent = "Close"
    close.className = "btn btn-sm btn-link"
    close.addEventListener("click", () => this.clearMarkerFocus())
    heading.append(close)
    wrapper.append(heading)
    marker.shipments.forEach(row => {
      const id = String(row.shipment_id)
      const item = document.createElement("div")
      item.className = "border-top py-2"
      this.addText(item, `${row.number} · ${row.client}`, "fw-semibold")
      if (this.runLookup.has(this.runFilter)) this.addText(item, `Stop ${this.stopPositions.get(this.runFilter).get(row.stop_key) + 1}`, "small fw-semibold")
      this.addText(item, `${row.order_number} · ${row.status} · ${row.location}`, "small fg-2")
      this.addText(item, row.address || "No delivery address", "small fg-2")
      this.addText(item, `Default run: ${row.default_run}`, "small mt-1")
      if (row.assigned_run) this.addText(item, `Current execution: ${row.assigned_run}`, "small")
      const showInList = document.createElement("button")
      showInList.type = "button"
      showInList.className = "btn btn-sm btn-link mt-2"
      showInList.textContent = "Show in list"
      showInList.setAttribute("aria-label", `Show ${row.number} in list`)
      showInList.addEventListener("click", () => {
        this.focusShipments([id])
        this.showView("list", true)
      })
      item.append(showInList)
      if (!this.readonlyValue) {
        const select = document.createElement("select")
        select.className = "form-control form-control-sm mt-2"
        select.setAttribute("aria-label", `Map proposed run for ${row.number}`)
        this.populateRuns(select, this.proposals[id])
        select.addEventListener("change", () => this.move([id], select.value))
        item.append(select)
        const button = document.createElement("button")
        button.type = "button"
        button.className = "btn btn-sm btn-outline theme-primary mt-2"
        button.dataset.selectShipment = id
        button.dataset.number = row.number
        button.addEventListener("click", () => { this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id); this.selectionChanged() })
        item.append(button)
      } else this.addText(item, `Proposed run: ${this.runLookup.get(this.proposals[id])?.name || "Unassigned"}`, "small fw-semibold")
      wrapper.append(item)
    })
    this.popup = wrapper
    this.refreshPopupSelection()
    return wrapper
  }

  move(ids, run) {
    if (this.readonlyValue || this.sorting || !ids.length) return
    ids.forEach(id => { this.proposals[id] = run || null })
    this.markDirty()
    this.refreshWorkspace()
  }

  selectionChanged() {
    if (!this.selected) return
    this.selectionTarget.textContent = `${this.selected.size} selected`
    this.bulkPanelTarget.hidden = this.selected.size === 0 || this.readonlyValue
    this.listTarget.querySelectorAll("[data-plan-selection]").forEach(input => { input.checked = this.selected.has(input.dataset.planSelection) })
    this.listTarget.querySelectorAll("[data-planning-shipment]").forEach(item => {
      const focused = this.focusedShipmentIds.has(item.dataset.planningShipment)
      item.classList.toggle("bg-subtle-primary", this.selected.has(item.dataset.planningShipment))
      item.classList.toggle("is-map-focused", focused)
      if (focused) item.setAttribute("aria-current", "location")
      else item.removeAttribute("aria-current")
    })
    if (this.mapInitialized) this.markers().forEach((marker, index) => {
      const data = this.markersValue[index]
      const selected = data.shipments.some(row => this.selected.has(String(row.shipment_id)))
      const focused = data.shipments.some(row => this.focusedShipmentIds.has(String(row.shipment_id)))
      const options = this.markerOptionsFor(data, selected || focused)
      marker.setIcon(options.icon)
      marker.setLabel(options.label)
      marker.setZIndex(focused ? google.maps.Marker.MAX_ZINDEX + 2 : selected ? google.maps.Marker.MAX_ZINDEX + 1 : null)
    })
    this.refreshPopupSelection()
  }

  markerClicked(data) {
    this.focusShipments(data.shipments.map(row => String(row.shipment_id)))
  }

  focusShipments(ids) {
    this.focusedShipmentIds = new Set(ids)
    this.revealFocusedShipment()
    this.selectionChanged()
  }

  revealFocusedShipment(preferredId, focus = false) {
    cancelAnimationFrame(this.focusScrollFrame)
    const rows = this.filteredRows()
    const focused = row => this.focusedShipmentIds.has(String(row.shipment_id))
    let index = rows.findIndex((row, index) => focused(row) && (preferredId ? String(row.shipment_id) === preferredId : Math.floor(index / 100) === this.page))
    if (index < 0) index = rows.findIndex(focused)
    if (index < 0) return
    const page = Math.floor(index / 100)
    if (page !== this.page) {
      this.page = page
      this.renderList()
      this.selectionChanged()
    }
    this.focusScrollFrame = requestAnimationFrame(() => {
      if (!this.listTarget.clientHeight) return
      if (!this.focusedShipmentIds.has(String(rows[index].shipment_id))) return
      const item = this.listTarget.querySelector(`[data-planning-shipment='${rows[index].shipment_id}']`)
      if (!item) return
      const rect = item.getBoundingClientRect(), list = this.listTarget.getBoundingClientRect()
      this.listTarget.scrollTop += rect.top - list.top - Math.max(0, (list.height - rect.height) / 2)
      if (focus) item.querySelector(".shipment-runs-customer")?.focus({ preventScroll: true })
    })
  }

  clearMarkerFocus() {
    cancelAnimationFrame(this.focusScrollFrame)
    this._infoWindow?.close()
    this.popup = null
    this.focusedShipmentIds.clear()
    this.selectionChanged()
  }

  refreshPopupSelection() {
    this.popup?.querySelectorAll("[data-select-shipment]").forEach(button => {
      const selected = this.selected.has(button.dataset.selectShipment)
      button.textContent = `${selected ? "Deselect" : "Select"} ${button.dataset.number}`
      button.setAttribute("aria-pressed", String(selected))
    })
  }

  generationGroups() {
    return this.runs.map(run => ({ ...run, rows: this.rows.filter(row => this.proposals[String(row.shipment_id)] === run.id) })).filter(run => run.rows.length)
  }

  renderGenerationSummary() {
    const groups = this.generationGroups()
    const assigned = groups.reduce((sum, run) => sum + run.rows.length, 0)
    if (this.hasGenerationSummaryTarget) this.generationSummaryTarget.textContent = `${assigned} assigned · ${this.rows.length - assigned} unassigned · ${groups.filter(run => !run.execution_id).length} executions to create · ${groups.filter(run => run.execution_id).length} planned executions to update`
    this.issuesTarget.replaceChildren()
    const issues = groups.filter(run => run.issue)
    issues.forEach(run => this.addText(this.issuesTarget, `${run.name}: ${run.issue}`, "small"))
    this.issuesTarget.hidden = issues.length === 0
    if (this.hasGenerateButtonTarget) this.generateButtonTarget.disabled = !assigned || groups.some(run => run.issue)
  }

  review() {
    this.reviewBodyTarget.replaceChildren()
    if (this.partialValue) this.addText(this.reviewBodyTarget, "Source filter active: timing estimates exclude stops outside this preview.", "small fg-warning mb-2")
    this.generationGroups().forEach(run => {
      const item = document.createElement("div")
      item.className = "border-top py-2"
      const stops = new Set(run.rows.map(row => row.stop_key)).size
      this.addText(item, `${run.name} · ${run.rows.length} shipments · ${stops} stops`, "fw-semibold")
      const state = this.estimates.get(run.id)
      const departure = this.settingsFor(run.id).start_time
      this.addText(item, departure ? `Planned departure ${departure} (${this.timeZoneValue})` : "Departure time not specified", "small mt-1")
      if (state?.schedule) this.addText(item, this.scheduleSummary(state.schedule), `small mt-1 ${state.schedule.lateStops || state.schedule.incomplete ? "fg-warning" : "fg-2"}`)
      else if (departure) this.addText(item, state?.message || "Timing estimates are not available yet. Review the run before dispatch.", "small fg-warning mt-1")
      if (this.stopOrders[run.id]) {
        const details = document.createElement("details")
        const summary = document.createElement("summary")
        summary.textContent = "Delivery stop order"
        summary.className = "small mt-1"
        details.append(summary)
        const list = document.createElement("ol")
        list.className = "small mt-2 mb-1"
        this.stopOrders[run.id].forEach(key => {
          const row = run.rows.find(row => row.stop_key === key)
          const stop = document.createElement("li")
          stop.textContent = `${row.client} · ${row.location}`
          list.append(stop)
        })
        details.append(list)
        item.append(details)
      }
      this.addText(item, run.execution_id ? "Update existing planned execution" : `Create planned execution · ${run.driver || "Driver not set"} · ${run.vehicle || "Vehicle not set"}`, "small fg-2")
      if (run.additional_execution) {
        const label = document.createElement("label")
        label.className = "d-flex align-items-start gap-2 small mt-2"
        const confirmation = document.createElement("input")
        confirmation.type = "checkbox"
        confirmation.name = "additional_run_ids[]"
        confirmation.setAttribute("form", "shipment-runs-plan")
        confirmation.value = run.id
        confirmation.className = "check"
        confirmation.addEventListener("change", () => this.updateAdditionalConfirmation())
        label.append(confirmation, document.createTextNode(`Create an additional ${run.name} execution for this date. Earlier executions stay unchanged.`))
        item.append(label)
      }
      this.reviewBodyTarget.append(item)
    })
    const unassigned = this.rows.filter(row => !this.proposals[String(row.shipment_id)])
    this.addText(this.reviewBodyTarget, `${unassigned.length} shipments will remain unassigned. ${unassigned.filter(row => row.current_run_id).length} will be removed from their current planned execution.`, "small mt-3")
    this.updateAdditionalConfirmation()
    window.bootstrap.Dialog.getOrCreateInstance(this.reviewDialogTarget).show()
  }

  updateAdditionalConfirmation() {
    this.reviewDialogTarget.querySelector('button[value="generate"]').disabled = [...this.reviewBodyTarget.querySelectorAll('input[name="additional_run_ids[]"]')].some(input => !input.checked)
  }

  searchChanged() { this.page = 0; this.selected.clear(); this.focusedShipmentIds.clear(); this.refreshWorkspace(); this.fitLocations() }
  clearSelection() { this.selected.clear(); this.selectionChanged() }
  assignSelected() { this.bulkRunTarget.value === "__defaults__" ? this.restoreDefaults() : this.move([...this.selected], this.bulkRunTarget.value) }
  restoreDefaults() {
    if (this.readonlyValue || this.sorting || !this.selected.size) return
    this.rows.filter(row => this.selected.has(String(row.shipment_id))).forEach(row => { this.proposals[String(row.shipment_id)] = row.default_run_id == null ? null : String(row.default_run_id) })
    this.markDirty()
    this.refreshWorkspace()
  }
  markDirty() { this.pendingSort = null; this.clearSortResult(); this.dirty = true; this.dirtyTarget.textContent = "Unsaved"; this.dirtyTarget.classList.add("is-dirty") }
  changeDate(event) { if (event.target.validity.valid) event.target.form.requestSubmit() }
  switchView(event) { this.showView(event.currentTarget.dataset.view) }
  showView(view, focusList = false) {
    this.layoutTarget.dataset.mobileView = view
    this.element.querySelectorAll("[data-view]").forEach(button => {
      button.classList.toggle("active", button.dataset.view === view)
      button.setAttribute("aria-pressed", String(button.dataset.view === view))
    })
    if (view === "map") requestAnimationFrame(() => this.fitLocations())
    else this.revealFocusedShipment(undefined, focusList)
  }
  focusShipment(event) {
    const id = String(event.currentTarget.dataset.shipmentId)
    const entry = this.shipmentMarkers?.get(id)
    if (!entry || this.mapTarget.hidden) return
    this.showView("map")
    requestAnimationFrame(() => { this.map().panTo(entry.marker.getPosition()); this.openMarker(entry.marker, entry.data); this.revealFocusedShipment(id) })
  }
  previousPage() { this.page = Math.max(0, this.page - 1); this.renderList(); this.selectionChanged(); this.listTarget.scrollTop = 0 }
  nextPage() { this.page += 1; this.renderList(); this.selectionChanged(); this.listTarget.scrollTop = 0 }
  submitting() { this.runSettingsFieldTarget.value = JSON.stringify(this.runSettings); this.dirty = false; this.assignmentsFieldTarget.value = JSON.stringify(this.proposals); this.stopOrdersFieldTarget.value = JSON.stringify(this.stopOrders) }
}
